<script setup lang="ts">
import {ref} from "vue";
import {Pill} from "@lucide/vue";
import MedicationCheckupCopiloteLine from "@/medication-checkup/components/MedicationCheckupCopiloteLine.vue";
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupProbeCard from "@/medication-checkup/components/MedicationCheckupProbeCard.vue";
import {SceneProps} from "@/medication-checkup/composables/useScene";
import {useProbeScene} from "@/medication-checkup/composables/useProbeScene";
import MedicationCheckupCopiloteBubble from "@/medication-checkup/components/MedicationCheckupCopiloteBubble.vue";
import MedicationCheckupSceneReplies from "@/medication-checkup/components/MedicationCheckupSceneReplies.vue";
import {ProbeView} from "@/medication-checkup/interfaces/probe";

/**
 * The scene of the galenic probe: do some drugs pose a practical problem by
 * their form — too big to swallow, hard to split, bad taste — and which ones.
 *
 * Everything it shows is served by the handler: the transition line, the
 * question and the difficulties proposed, the retained treatments, and —
 * once validated — the answer with the findings raised and the closing line.
 * The mechanic is the one every probe plays, held by the shared composable.
 */

const props = defineProps<SceneProps<ProbeView>>();

const root = ref<HTMLElement | null>(null);

const {
    bubbleShown,
    cardShown,
    clicked,
    answer,
    drafts,
    view,
    text,
    actions,
    closing,
    acted,
    blocked,
    flagged,
    warning,
    flag,
    give,
    act
} = useProbeScene(props, root);
</script>

<template>
  <div ref="root" class="flex flex-col gap-3.5">
    <template v-if="frozen">
      <MedicationCheckupCopiloteLine v-if="text" :text="text" />
      <MedicationCheckupProbeCard v-if="view" :view="view" :icon="Pill" frozen />
      <MedicationCheckupUserEcho v-if="acted" :text="acted" />
      <MedicationCheckupCopiloteLine v-if="closing" :text="closing" />
    </template>

    <template v-else>
      <MedicationCheckupCopiloteBubble
        :shown="bubbleShown"
        :text="text"
      />

      <MedicationCheckupProbeCard
        v-if="cardShown && view"
        :view="view"
        :icon="Pill"
        :answer="answer"
        :drafts="drafts"
        :flagged="flagged"
        class="animate-message-in"
        @answer="give"
        @update:drafts="drafts = $event"
      />

      <MedicationCheckupSceneReplies
        :clicked="clicked"
        :actions="actions"
        :shown="cardShown"
        :blocked="blocked"
        :warning="warning"
        @act="act"
        @blocked="flag"
      />
    </template>
  </div>
</template>
